import React, { useState, useMemo, useCallback } from 'react';
import { useBts } from '@/context/btsContext/BtsContextProvider';
import { FaRegEdit, FaTrash } from 'react-icons/fa';
import { updateManualProduct } from '@/services/BtsService';

// --- Grip Icon ---
const GripIcon = () => (
  <svg className="w-4 h-4 text-gray-500 inline-block align-middle pointer-events-none" fill="currentColor" viewBox="0 0 24 24">
    <path d="M8.5 7a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zm5 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zm-5 5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zm5 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zm-5 5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zm5 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z" />
  </svg>
);

// --- Row Component ---
const ProductRow = React.memo(function ProductRow({
  row,
  idx,
  tableHeadersLength,
  selectedIds,
  handleSelectRow,
  handleEdit,
  handleDelete,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  draggedIndex,
  activeDragRowId,
  setActiveDragRowId,
}) {
  const qty = Number(row.quantity) || 0;
  const unitCost = Number(row.price) / (Number(row.conversionFactor) || 1);
  let exSell = Number(row.exSell) || 0;
  const rowGp = row.gp !== undefined && row.gp !== null && row.gp !== '' ? Number(row.gp) : null;

  if (exSell === 0 && rowGp !== null && !isNaN(rowGp) && rowGp < 100 && unitCost > 0) {
    exSell = Math.round((unitCost / (1 - rowGp / 100)) * 10000) / 10000;
  }

  const isExempt =
    row.taxCode === 'E' ||
    (row.exSell != null &&
      row.incSell != null &&
      Math.abs(Number(row.incSell) - Number(row.exSell)) < 0.0001 &&
      Number(row.exSell) > 0);

  const incSell = isExempt ? exSell : exSell * 1.1;

  const totalExSell = exSell * qty;
  const totalIncSell = incSell * qty;
  const totalAvgCost = unitCost * qty;

  const gpDollar = totalExSell - totalAvgCost;
  const gpPercent = totalExSell !== 0 ? (gpDollar / totalExSell) * 100 : (rowGp !== null ? rowGp : 0);

  const isRowDraggable = activeDragRowId === row.id;

  const displayHeaders = useMemo(() => {
    if (!row.header || !row.header.trim()) {
      return [];
    }
    if (Array.isArray(row.headers) && row.headers.length > 0) {
      return row.headers
        .filter((h) => h && h.header && String(h.header).trim() !== '')
        .map((h, idx) => (idx === 0 ? { ...h, header: row.header.trim() } : h));
    }
    return [{ header: row.header.trim() }];
  }, [row.header, row.headers]);

  return (
    <>
      {displayHeaders.map((h, hIdx) => (
        <tr key={h.id || `header-${hIdx}`} className="bg-blue-100 text-xs border-b border-gray-200">
          <td
            colSpan={tableHeadersLength}
            className="px-2 py-2 text-left font-bold text-blue-800">
            {h.header}
          </td>
        </tr>
      ))}
      <tr
        className={`bg-white text-sm border-b border-gray-200 ${isRowDraggable ? 'cursor-grabbing' : ''
          } ${draggedIndex === idx ? 'opacity-50 border-2 border-blue-500' : ''
          }`}
        draggable={isRowDraggable}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragOver={onDragOver}
        onDrop={onDrop}
      >
        <td className="px-2 py-2">
          <input
            type="checkbox"
            checked={selectedIds.includes(row.id)}
            onChange={() => handleSelectRow(row.id)}
          />
        </td>
        <td className="px-2 py-2 whitespace-nowrap">
          <span
            className="cursor-grab active:cursor-grabbing inline-block mr-2 p-1 select-none rounded hover:bg-gray-100 transition-colors"
            title="Drag to reorder"
            onMouseDown={() => setActiveDragRowId(row.id)}
            onMouseUp={() => setActiveDragRowId(null)}
          >
            <GripIcon />
          </span>
          <span>{row.sequenceNumber}</span>
        </td>
        <td className="px-2 py-2 text-xs text-left select-text">{row.itemCode}</td>

        <td className="px-2 py-2 text-sm min-w-40 text-left select-text">
          {row.description}
        </td>
        <td className="px-2 py-2 select-text">{row.unit}</td>
        <td className="px-2 py-2 select-text">{row.quantity}</td>
        <td className="px-2 py-2 select-text">{unitCost.toFixed(2)}</td>
        <td className="px-2 py-2 select-text">{gpPercent.toFixed(2)}%</td>
        <td className="px-2 py-2 select-text">{exSell.toFixed(2)}</td>
        <td className="px-2 py-2 select-text">{incSell.toFixed(2)}</td>
        <td className="px-2 py-2 select-text">{totalIncSell.toFixed(2)}</td>
        <td className="px-2 py-2 whitespace-nowrap text-sm font-medium">
          <div className="flex items-center space-x-2 justify-center">
            <button
              type="button"
              title="Edit"
              className="text-blue-600 hover:text-blue-900"
              onClick={() => handleEdit(row)}>
              <FaRegEdit size={18} />
            </button>
            <button
              type="button"
              title="Delete"
              className="text-red-600 hover:text-red-900"
              onClick={() => handleDelete(row)}>
              <FaTrash className="text-lg" />
            </button>
          </div>
        </td>
      </tr>
    </>
  );
});

// --- Main Table ---
const ProposalProductTable = ({ proposalId, numberOfStudents, booklistType, classId }) => {
  const {
    btsClassProdData,
    setBtsProduct,
    deleteClassProduct,
    fetchBtsProductsByClass,
    deleteMultipleProductByClass,
  } = useBts();

  const [selectedIds, setSelectedIds] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [activeDragRowId, setActiveDragRowId] = useState(null);

  // Bulk delete
  const getClassId = () => {
    if (!selectedIds.length) return null;
    const row = (btsClassProdData || []).find((r) => r.id === selectedIds[0]);
    return row ? row.btsClassId : null;
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    const activeClassId = classId || getClassId();
    if (!activeClassId) return;
    try {
      await deleteMultipleProductByClass(proposalId, activeClassId, [...selectedIds]);
      setSelectedIds([]);
      await fetchBtsProductsByClass(proposalId, activeClassId);
    } catch { }
  };

  const handleSelectAll = () => {
    if (
      Array.isArray(btsClassProdData) &&
      btsClassProdData.length > 0 &&
      selectedIds.length === btsClassProdData.length
    ) {
      setSelectedIds([]);
    } else if (Array.isArray(btsClassProdData) && btsClassProdData[0] !== 'No Data Found') {
      setSelectedIds(btsClassProdData.map((row) => row.id));
    }
  };

  const handleSelectRow = useCallback(
    (id) => {
      setSelectedIds((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
      );
    },
    [setSelectedIds],
  );

  const handleEdit = useCallback(
    (row) => {
      const original = (btsClassProdData || []).find((p) => p.id === row.id);
      const productToEdit = original || row;
      const effectiveHeader = productToEdit.header || '';
      setBtsProduct({
        ...productToEdit,
        header: effectiveHeader,
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [setBtsProduct, btsClassProdData],
  );

  const handleDelete = useCallback(
    async (row) => {
      try {
        const activeClassId = classId || row.btsClassId;
        await deleteClassProduct(proposalId, activeClassId, row.id);
        await fetchBtsProductsByClass(proposalId, activeClassId);
      } catch { }
    },
    [deleteClassProduct, proposalId, classId, fetchBtsProductsByClass],
  );

  const handleDragStart = useCallback((e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.setData('text/plain', index);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback(
    async (e, targetIndex) => {
      e.preventDefault();
      if (draggedIndex === null || draggedIndex === targetIndex) {
        setActiveDragRowId(null);
        setDraggedIndex(null);
        return;
      }

      const activeClassId = classId || (btsClassProdData && btsClassProdData[0]?.btsClassId);
      if (!activeClassId) {
        setActiveDragRowId(null);
        setDraggedIndex(null);
        return;
      }

      try {
        const draggedRow = btsClassProdData[draggedIndex];
        const targetRow = btsClassProdData[targetIndex];
        if (!targetRow) return;

        let newSeq;
        if (draggedIndex < targetIndex) {
          // Dragging down: place after targetRow
          newSeq = (targetRow.sequenceNumber || 0) + 1;
        } else {
          // Dragging up: place before targetRow
          newSeq = targetRow.sequenceNumber || 1;
        }

        const payload = {
          ...draggedRow,
          sequenceNumber: newSeq,
        };
        await updateManualProduct(
          proposalId,
          activeClassId,
          payload,
          'Product sequence updated successfully',
        );
        await fetchBtsProductsByClass(proposalId, activeClassId);
      } catch (error) {
        console.error('Failed to update sequence', error);
      } finally {
        setDraggedIndex(null);
        setActiveDragRowId(null);
      }
    },
    [draggedIndex, btsClassProdData, classId, proposalId, fetchBtsProductsByClass],
  );

  // --- Footer Totals ---
  const totals = useMemo(() => {
    let totalExSell = 0;
    let totalIncSell = 0;
    let totalAvgCost = 0;
    let totalGpDollar = 0;

    if (Array.isArray(btsClassProdData) && btsClassProdData[0] !== 'No Data Found') {
      btsClassProdData.forEach((row) => {
        const qty = Number(row.quantity) || 0;
        const unitCost = Number(row.price) / (Number(row.conversionFactor) || 1);
        let exSell = Number(row.exSell) || 0;
        const rowGp = row.gp !== undefined && row.gp !== null && row.gp !== '' ? Number(row.gp) : null;

        if (exSell === 0 && rowGp !== null && !isNaN(rowGp) && rowGp < 100 && unitCost > 0) {
          exSell = Math.round((unitCost / (1 - rowGp / 100)) * 10000) / 10000;
        }

        const isExempt =
          row.taxCode === 'E' ||
          (row.exSell != null &&
            row.incSell != null &&
            Math.abs(Number(row.incSell) - Number(row.exSell)) < 0.0001 &&
            Number(row.exSell) > 0);

        const incSell = isExempt ? exSell : exSell * 1.1;

        const rowTotalExSell = Math.round(exSell * qty * 100) / 100;
        const rowTotalIncSell = Math.round(incSell * qty * 100) / 100;
        const rowTotalAvgCost = Math.round(unitCost * qty * 100) / 100;

        totalExSell += rowTotalExSell;
        totalIncSell += rowTotalIncSell;
        totalAvgCost += rowTotalAvgCost;
      });

      totalGpDollar = totalExSell - totalAvgCost;
    }

    if (booklistType === 'Bulk' && numberOfStudents && numberOfStudents > 0) {
      const multiplier = Number(numberOfStudents);
      totalExSell *= multiplier;
      totalIncSell *= multiplier;
      totalAvgCost *= multiplier;
      totalGpDollar *= multiplier;
    }

    const gpPercent =
      totalExSell !== 0 ? (totalGpDollar / totalExSell) * 100 : 0;

    return { totalExSell, totalIncSell, totalGpDollar, gpPercent };
  }, [btsClassProdData, booklistType, numberOfStudents]);

  const tableHeaders = [
    '',
    'Seq No',
    'Item Code',
    'Description',
    'UOM',
    'Qty',
    'BTS Cost',
    'GP %',
    'Ex-Sell',
    'Inc-Sell',
    'Total',
    'Actions',
  ];

  const rowCount = Array.isArray(btsClassProdData) ? btsClassProdData.length : 0;
  const scrollNeeded = rowCount > 8 || rowCount * 50 > window.innerHeight * 0.7;

  const tableRows = useMemo(() => {
    if (
      !btsClassProdData ||
      btsClassProdData.length === 0 ||
      btsClassProdData[0] === 'No Data Found'
    ) {
      return (
        <tr>
          <td
            colSpan={tableHeaders.length}
            className="text-center py-4 text-gray-500">
            No Data Found
          </td>
        </tr>
      );
    }
    return btsClassProdData.map((row, idx) => (
      <ProductRow
        key={row.id || idx}
        row={row}
        idx={idx}
        tableHeadersLength={tableHeaders.length}
        selectedIds={selectedIds}
        handleSelectRow={handleSelectRow}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
        onDragStart={(e) => handleDragStart(e, idx)}
        onDragEnd={() => {
          setActiveDragRowId(null);
          setDraggedIndex(null);
        }}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, idx)}
        draggedIndex={draggedIndex}
        activeDragRowId={activeDragRowId}
        setActiveDragRowId={setActiveDragRowId}
      />
    ));
  }, [
    btsClassProdData,
    selectedIds,
    handleSelectRow,
    handleEdit,
    handleDelete,
    tableHeaders.length,
    draggedIndex,
    activeDragRowId,
    handleDragStart,
    handleDragOver,
    handleDrop,
  ]);

  return (
    <div className="container mx-auto mb-5 mt-5 pr-5">
      <div className="flex justify-end items-center gap-3 mb-2 mt-2">
        <button
          className="bg-red-500 hover:bg-red-600 text-white px-4 rounded disabled:opacity-50 text-sm font-semibold h-10 flex items-center justify-center transition-colors"
          onClick={handleBulkDelete}
          disabled={
            selectedIds.length === 0 ||
            (Array.isArray(btsClassProdData) &&
              btsClassProdData[0] === 'No Data Found')
          }
        >
          Delete Selected
        </button>
      </div>
      <div
        className={`relative ${scrollNeeded ? 'max-h-[70vh] overflow-y-auto' : ''}`}>
        <table className="min-w-full bg-white border border-gray-200">
          <thead
            className={`bg-gray-200 font-400 text-left min-w-full ${scrollNeeded ? 'sticky top-0 z-10' : ''}`}>
            <tr>
              {tableHeaders.map((header, idx) => (
                <th
                  key={idx}
                  className="px-2 py-3 border-b text-left text-xs font-medium text-gray-500 uppercase">
                  {idx === 0 ? (
                    <input
                      type="checkbox"
                      checked={
                        Array.isArray(btsClassProdData) &&
                        btsClassProdData.length > 0 &&
                        selectedIds.length === btsClassProdData.length
                      }
                      onChange={handleSelectAll}
                      disabled={
                        !Array.isArray(btsClassProdData) ||
                        btsClassProdData.length === 0 ||
                        btsClassProdData[0] === 'No Data Found'
                      }
                    />
                  ) : (
                    header
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="text-center min-w-full font-600">{tableRows}</tbody>
        </table>
      </div>

      {/* --- Footer Totals --- */}
      <div className="flex justify-end gap-10 mt-2 pr-10 text-lg font-semibold ">
        <div>GP: {totals.gpPercent.toFixed(2)}%</div>
        <div>Total GP: ${totals.totalGpDollar.toFixed(2)}</div>
        <div>Total Sum: ${totals.totalIncSell.toFixed(2)}</div>
      </div>
    </div>
  );
};

export default ProposalProductTable;
